// src/components/modal/realtime/usePedidoStatus.ts
import { useEffect, useRef, useState } from "react";
import { useApi } from "../../../hooks/useApi";
import { parseOrderStatus } from "../../../lib/orderStatus";

/**
 * Hook para consultar o status do pedido via API REST
 * Faz polling a cada 10 segundos para atualizar o status
 * Usa useApi para ter base URL, Bearer e renovação do guest em 401
 */
export function usePedidoStatus(pedidoId: string | null) {
  const [status, setStatus] = useState(0);
  const [loading, setLoading] = useState(false);
  const api = useApi();

  // Ref para não reiniciar o polling quando a instância do api mudar
  const apiRef = useRef(api);
  apiRef.current = api;

  useEffect(() => {
    if (!pedidoId || pedidoId === "0") {
      console.log("⚠️ usePedidoStatus: pedidoId inválido:", pedidoId);
      return;
    }

    console.log("🔍 usePedidoStatus: Iniciando para pedidoId:", pedidoId);

    const fetchOrderStatus = async () => {
      try {
        setLoading(true);

        // A API retorna o id do status como número puro (ex.: 3)
        const data = await apiRef.current.get(`/orders/${pedidoId}/status`);
        const orderStatus = parseOrderStatus(data);

        if (orderStatus !== null) {
          setStatus(orderStatus);
          localStorage.setItem("pedido.status", String(orderStatus));

          // Se finalizado ou cancelado (4 ou 5), marca como entregue
          if (orderStatus >= 4) {
            localStorage.setItem("pedido.delivery", String(1));
          }

          console.log(`✅ Status do pedido ${pedidoId}: ${orderStatus}`);
        } else {
          console.warn("⚠️ Status inválido:", data);
        }
      } catch (error) {
        console.error("❌ Erro ao consultar status do pedido:", error);
      } finally {
        setLoading(false);
      }
    };

    // Busca imediatamente ao montar/atualizar pedidoId
    fetchOrderStatus();

    // Configura polling a cada 10 segundos
    const interval = setInterval(fetchOrderStatus, 10000);

    // Cleanup: remove interval ao desmontar ou mudar pedidoId
    return () => {
      console.log(
        "🧹 usePedidoStatus: Limpando interval para pedidoId:",
        pedidoId
      );
      clearInterval(interval);
    };
  }, [pedidoId]);

  return { status, loading };
}
